import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageCard } from '../../components/layout/page/PageCard';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import {
  CollaboratorDependent,
  CollaboratorEducation,
  CollaboratorProfile,
} from '../../services/collaboratorProfileApi';
import {
  BRAZILIAN_STATE_OPTIONS,
  DISABILITY_TYPE_OPTIONS,
  EDUCATION_DEGREE_OPTIONS,
  EDUCATION_STATUS_OPTIONS,
  EDUCATION_TYPE_OPTIONS,
  GENDER_OPTIONS,
  JOB_LEVEL_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  RACE_ETHNICITY_OPTIONS,
  SEXUAL_ORIENTATION_OPTIONS,
} from '../../services/collaboratorProfileOptions';
import {
  fetchMyCollaboratorProfile,
  updateMyCollaboratorRegistration,
} from '../../services/myCollaboratorApi';

type DependentFormRow = CollaboratorDependent & { key: string };
type EducationFormRow = CollaboratorEducation & { key: string };

function createDependentRow(): DependentFormRow {
  return {
    key: crypto.randomUUID(),
    name: '',
    birthDate: null,
    relationship: '',
    cpf: '',
  };
}

function createEducationRow(): EducationFormRow {
  return {
    key: crypto.randomUUID(),
    course: '',
    educationType: 'Graduacao',
    educationDegree: 'Superior',
    institution: '',
    educationStatus: 'EmAndamento',
  };
}

function jobLevelLabel(value?: string | null): string {
  if (!value) return '—';
  return JOB_LEVEL_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

export function MyRegistrationSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hrSummary, setHrSummary] = useState({
    cpf: '',
    cnpj: '',
    employmentType: '',
    jobPositionName: '',
    jobLevel: '',
  });
  const [form, setForm] = useState({
    birthDate: '',
    raceEthnicity: '',
    gender: '',
    sexualOrientation: '',
    isPersonWithDisability: false,
    disabilityTypes: [] as string[],
    address: '',
    postalCode: '',
    city: '',
    state: '',
    maritalStatus: '',
    phone: '',
    personalEmail: '',
  });
  const [dependents, setDependents] = useState<DependentFormRow[]>([]);
  const [educations, setEducations] = useState<EducationFormRow[]>([]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const profile = await fetchMyCollaboratorProfile();
        applyProfile(profile);
      } catch {
        toast.error('Falha ao carregar o cadastro.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function applyProfile(profile: CollaboratorProfile): void {
    setHrSummary({
      cpf: profile.cpf ?? '',
      cnpj: profile.cnpj ?? '',
      employmentType: profile.employmentType ?? '',
      jobPositionName: profile.jobPositionName ?? '',
      jobLevel: profile.jobLevel ?? '',
    });
    setForm({
      birthDate: profile.birthDate ?? '',
      raceEthnicity: profile.raceEthnicity ?? '',
      gender: profile.gender ?? '',
      sexualOrientation: profile.sexualOrientation ?? '',
      isPersonWithDisability: profile.isPersonWithDisability ?? false,
      disabilityTypes: [...(profile.disabilityTypes ?? [])],
      address: profile.address ?? '',
      postalCode: profile.postalCode ?? '',
      city: profile.city ?? '',
      state: profile.state ?? '',
      maritalStatus: profile.maritalStatus ?? '',
      phone: profile.phone ?? '',
      personalEmail: profile.personalEmail ?? '',
    });
    setDependents(
      profile.dependents.map((item) => ({
        ...item,
        key: item.id ?? crypto.randomUUID(),
        birthDate: item.birthDate ?? null,
        relationship: item.relationship ?? '',
        cpf: item.cpf ?? '',
      })),
    );
    setEducations(
      profile.educations.map((item) => ({
        ...item,
        key: item.id ?? crypto.randomUUID(),
        educationDegree: item.educationDegree ?? '',
        institution: item.institution ?? '',
      })),
    );
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (form.isPersonWithDisability && form.disabilityTypes.length === 0) {
      toast.error('Selecione ao menos um tipo de deficiência.');
      return;
    }

    setSaving(true);
    try {
      const saved = await updateMyCollaboratorRegistration({
        birthDate: form.birthDate || null,
        raceEthnicity: form.raceEthnicity,
        gender: form.gender,
        sexualOrientation: form.sexualOrientation,
        isPersonWithDisability: form.isPersonWithDisability,
        disabilityTypes: form.disabilityTypes,
        address: form.address,
        postalCode: form.postalCode,
        city: form.city,
        state: form.state,
        maritalStatus: form.maritalStatus,
        phone: form.phone,
        personalEmail: form.personalEmail,
        dependents: dependents.map((item) => ({
          name: item.name,
          birthDate: item.birthDate,
          relationship: item.relationship,
          cpf: item.cpf,
        })),
        educations: educations.map((item) => ({
          course: item.course,
          educationType: item.educationType,
          educationDegree: item.educationDegree ?? undefined,
          institution: item.institution,
          educationStatus: item.educationStatus,
        })),
      });
      applyProfile(saved);
      toast.success('Cadastro atualizado.');
    } catch {
      toast.error('Não foi possível salvar o cadastro.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-secondary-500">Carregando cadastro...</p>;
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
      <PageCard
        title="Dados profissionais (somente leitura)"
        subtitle="Cargo e vínculo são definidos pela empresa."
      >
        <div className="erp-form-grid md:grid-cols-3">
          <input className="erp-input-compact" disabled value={hrSummary.jobPositionName || 'Cargo não definido'} />
          <input className="erp-input-compact" disabled value={jobLevelLabel(hrSummary.jobLevel)} />
          <input className="erp-input-compact" disabled value={hrSummary.employmentType || 'Tipo de vínculo'} />
          <input className="erp-input-compact" disabled value={hrSummary.cpf || 'CPF'} />
          <input className="erp-input-compact" disabled value={hrSummary.cnpj || 'CNPJ'} />
        </div>
      </PageCard>

      <PageCard title="Dados pessoais">
        <div className="erp-form-grid md:grid-cols-3">
          <input
            className="erp-input-compact"
            type="date"
            value={form.birthDate}
            onChange={(event) => setForm({ ...form, birthDate: event.target.value })}
          />
          <select
            className="erp-input-compact"
            value={form.raceEthnicity}
            onChange={(event) => setForm({ ...form, raceEthnicity: event.target.value })}
          >
            <option value="">Raça/etnia</option>
            {RACE_ETHNICITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <select
            className="erp-input-compact"
            value={form.gender}
            onChange={(event) => setForm({ ...form, gender: event.target.value })}
          >
            <option value="">Gênero</option>
            {GENDER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <select
            className="erp-input-compact"
            value={form.sexualOrientation}
            onChange={(event) => setForm({ ...form, sexualOrientation: event.target.value })}
          >
            <option value="">Orientação sexual</option>
            {SEXUAL_ORIENTATION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <select
            className="erp-input-compact"
            value={form.maritalStatus}
            onChange={(event) => setForm({ ...form, maritalStatus: event.target.value })}
          >
            <option value="">Estado civil</option>
            {MARITAL_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <input
            className="erp-input-compact"
            placeholder="Telefone"
            value={form.phone}
            onChange={(event) => setForm({ ...form, phone: event.target.value })}
          />
          <input
            className="erp-input-compact md:col-span-2"
            placeholder="E-mail pessoal"
            value={form.personalEmail}
            onChange={(event) => setForm({ ...form, personalEmail: event.target.value })}
          />
          <label className="erp-label-inline md:col-span-3">
            <input
              type="checkbox"
              checked={form.isPersonWithDisability}
              onChange={(event) =>
                setForm({
                  ...form,
                  isPersonWithDisability: event.target.checked,
                  disabilityTypes: event.target.checked ? form.disabilityTypes : [],
                })
              }
            />
            Pessoa com deficiência
          </label>
          {form.isPersonWithDisability ? (
            <div className="md:col-span-3 flex flex-wrap gap-3">
              {DISABILITY_TYPE_OPTIONS.map((option) => (
                <label key={option.value} className="erp-label-inline">
                  <input
                    type="checkbox"
                    checked={form.disabilityTypes.includes(option.value)}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        disabilityTypes: event.target.checked
                          ? [...current.disabilityTypes, option.value]
                          : current.disabilityTypes.filter((item) => item !== option.value),
                      }));
                    }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          ) : null}
        </div>
      </PageCard>

      <PageCard title="Endereço">
        <div className="erp-form-grid md:grid-cols-4">
          <input
            className="erp-input-compact md:col-span-2"
            placeholder="Endereço"
            value={form.address}
            onChange={(event) => setForm({ ...form, address: event.target.value })}
          />
          <input
            className="erp-input-compact"
            placeholder="CEP"
            value={form.postalCode}
            onChange={(event) => setForm({ ...form, postalCode: event.target.value })}
          />
          <input
            className="erp-input-compact"
            placeholder="Cidade"
            value={form.city}
            onChange={(event) => setForm({ ...form, city: event.target.value })}
          />
          <select
            className="erp-input-compact"
            value={form.state}
            onChange={(event) => setForm({ ...form, state: event.target.value })}
          >
            <option value="">UF</option>
            {BRAZILIAN_STATE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </PageCard>

      <PageCard title="Dependentes">
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            className="erp-btn-secondary"
            onClick={() => setDependents((current) => [...current, createDependentRow()])}
          >
            Adicionar
          </button>
        </div>
        {dependents.length === 0 ? (
          <p className="text-sm text-secondary-500">Nenhum dependente cadastrado.</p>
        ) : (
          <div className="space-y-3">
            {dependents.map((dependent, index) => (
              <div key={dependent.key} className="erp-form-grid md:grid-cols-5 items-end gap-2">
                <input
                  className="erp-input-compact"
                  placeholder="Nome"
                  required
                  value={dependent.name}
                  onChange={(event) => {
                    const value = event.target.value;
                    setDependents((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, name: value } : item,
                      ),
                    );
                  }}
                />
                <input
                  className="erp-input-compact"
                  type="date"
                  value={dependent.birthDate ?? ''}
                  onChange={(event) => {
                    const value = event.target.value || null;
                    setDependents((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, birthDate: value } : item,
                      ),
                    );
                  }}
                />
                <input
                  className="erp-input-compact"
                  placeholder="Parentesco"
                  value={dependent.relationship}
                  onChange={(event) => {
                    const value = event.target.value;
                    setDependents((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, relationship: value } : item,
                      ),
                    );
                  }}
                />
                <input
                  className="erp-input-compact"
                  placeholder="CPF"
                  value={dependent.cpf}
                  onChange={(event) => {
                    const value = event.target.value;
                    setDependents((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, cpf: value } : item,
                      ),
                    );
                  }}
                />
                <DeleteIconButton
                  label="Remover dependente"
                  onClick={() =>
                    setDependents((current) => current.filter((_, itemIndex) => itemIndex !== index))
                  }
                />
              </div>
            ))}
          </div>
        )}
      </PageCard>

      <PageCard title="Formação">
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            className="erp-btn-secondary"
            onClick={() => setEducations((current) => [...current, createEducationRow()])}
          >
            Adicionar
          </button>
        </div>
        {educations.length === 0 ? (
          <p className="text-sm text-secondary-500">Nenhuma formação cadastrada.</p>
        ) : (
          <div className="space-y-3">
            {educations.map((education, index) => (
              <div key={education.key} className="erp-form-grid md:grid-cols-3 gap-2 border border-secondary-200 rounded-lg p-3">
                <input
                  className="erp-input-compact"
                  placeholder="Curso"
                  required
                  value={education.course}
                  onChange={(event) => {
                    const value = event.target.value;
                    setEducations((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, course: value } : item,
                      ),
                    );
                  }}
                />
                <select
                  className="erp-input-compact"
                  value={education.educationType}
                  onChange={(event) => {
                    const value = event.target.value;
                    setEducations((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, educationType: value } : item,
                      ),
                    );
                  }}
                >
                  {EDUCATION_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <select
                  className="erp-input-compact"
                  value={education.educationDegree ?? ''}
                  onChange={(event) => {
                    const value = event.target.value;
                    setEducations((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, educationDegree: value } : item,
                      ),
                    );
                  }}
                >
                  <option value="">Grau</option>
                  {EDUCATION_DEGREE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <input
                  className="erp-input-compact"
                  placeholder="Instituição"
                  value={education.institution}
                  onChange={(event) => {
                    const value = event.target.value;
                    setEducations((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, institution: value } : item,
                      ),
                    );
                  }}
                />
                <div className="flex items-end gap-2">
                  <select
                    className="erp-input-compact flex-1"
                    value={education.educationStatus}
                    onChange={(event) => {
                      const value = event.target.value;
                      setEducations((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, educationStatus: value } : item,
                        ),
                      );
                    }}
                  >
                    {EDUCATION_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <DeleteIconButton
                    label="Remover formação"
                    onClick={() =>
                      setEducations((current) =>
                        current.filter((_, itemIndex) => itemIndex !== index),
                      )
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </PageCard>

      <div className="erp-btn-actions">
        <button type="submit" className="erp-btn-primary" disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar cadastro'}
        </button>
      </div>
    </form>
  );
}
